import { AlertTriangle, CheckCircle2, Pencil } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { AccountHold, HoldReason, SavingsAccount } from '@/api/savings-types'
import { MoneyDisplay } from '@/components/money-display'
import { MoneyInput } from '@/components/money-input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatDateTime } from '@/lib/datetime'
import type { Pesewas } from '@/lib/money'

import { HOLD_REASONS, holdReasonDescription, holdReasonLabel } from './constants'
import { usePlaceAccountHold, useReleaseAccountHold } from './mutations'
import { SavingsActionDialog } from './savings-action-dialog'

/**
 * Placing — or amending — a hold.
 *
 * The endpoint is not "create a hold". The aggregate looks for an active hold carrying the same
 * reason and revises it in place: the amount and the note are overwritten, and `placedAtUtc` stays
 * as it was. Two consequences shape this dialog.
 *
 * The **ceiling** is `ledgerBalance − holdAmount + existingHoldOfThisReason`, not
 * `ledgerBalance − holdAmount`. Using the narrower figure would block a legitimate increase to a
 * lien that is already in place, client-side, for no reason the operator could see.
 *
 * The **note** on an existing hold is about to be destroyed, and the note is the only explanation a
 * reviewer will have years later. So the dialog shows the note that is there and says plainly that
 * confirming replaces it — a second lien for a genuinely different matter has to release the first
 * one and say why, or the record of the first matter simply vanishes.
 */
export function PlaceHoldDialog({
  account,
  open,
  onOpenChange,
}: {
  account: SavingsAccount
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [reason, setReason] = useState<HoldReason>('Lien')
  const [amount, setAmount] = useState<Pesewas | null>(null)
  const [note, setNote] = useState('')
  const [placed, setPlaced] = useState<{ hold: AccountHold; amended: boolean } | null>(null)

  const place = usePlaceAccountHold(account.id)

  const existing = account.holds.find((hold) => hold.isActive && hold.reason === reason)

  // The server's own ceiling, reproduced exactly: the unheld balance, plus back whatever the hold
  // being amended already holds.
  const ceiling = account.ledgerBalanceMinor - account.holdAmountMinor + (existing?.amountMinor ?? 0)

  useEffect(() => {
    if (!open) {
      setReason('Lien')
      setAmount(null)
      setNote('')
      setPlaced(null)
      place.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // The note and amount of the hold being amended are the starting point, so an operator raising a
  // lien by a hundred cedis is editing it rather than retyping it from memory.
  useEffect(() => {
    if (!open) {
      return
    }
    setAmount(existing?.amountMinor ?? null)
    setNote(existing?.note ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reason])

  const trimmedNote = note.trim()
  const overCeiling = amount !== null && amount > ceiling

  return (
    <SavingsActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title={existing ? 'Amend the hold' : 'Place a hold'}
      description={
        existing
          ? `This account already has an active ${holdReasonLabel(reason).toLowerCase()} hold. Confirming revises it rather than adding a second.`
          : 'A hold earmarks part of the balance. It reduces what may be withdrawn and nothing else.'
      }
      notice={
        existing ? (
          <Alert tone="warning">
            <Pencil aria-hidden />
            <div className="space-y-1">
              <AlertTitle>This replaces the hold that is already there</AlertTitle>
              <AlertDescription>
                <MoneyDisplay
                  value={existing.amountMinor}
                  currency={existing.currencyCode}
                  className="inline text-left"
                />
                , placed {formatDateTime(existing.placedAtUtc)}, noted as
                {existing.note.trim() === '' ? ' nothing' : ` “${existing.note}”`}. The amount and
                the note below overwrite it; the original timestamp stays. If this is a different
                matter from that one, release that hold first — otherwise the reason it was placed is
                gone from the record.
              </AlertDescription>
            </div>
          </Alert>
        ) : null
      }
      outcome={
        placed ? (
          <Alert tone="success">
            <CheckCircle2 aria-hidden />
            <div className="space-y-1">
              <AlertTitle>{placed.amended ? 'Hold amended' : 'Hold placed'}</AlertTitle>
              <AlertDescription>
                <MoneyDisplay
                  value={placed.hold.amountMinor}
                  currency={placed.hold.currencyCode}
                  className="inline text-left"
                />{' '}
                is earmarked under {holdReasonLabel(placed.hold.reason).toLowerCase()}. The available
                balance above has been re-read from the server rather than calculated here.
              </AlertDescription>
            </div>
          </Alert>
        ) : null
      }
      confirmLabel={existing ? 'Amend hold' : 'Place hold'}
      isPending={place.isPending}
      disabled={amount === null || amount <= 0 || trimmedNote === '' || overCeiling}
      error={place.isError ? place.error : null}
      onConfirm={() => {
        if (amount === null) {
          return
        }

        place.mutate(
          { amountMinor: amount, currencyCode: account.currencyCode, reason, note: trimmedNote },
          {
            onSuccess: (hold) => {
              setPlaced({ hold, amended: existing !== undefined })
            },
          },
        )
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="hold-reason">Reason</Label>
        <Select value={reason} onValueChange={(next) => setReason(next as HoldReason)}>
          <SelectTrigger id="hold-reason">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HOLD_REASONS.map((candidate) => (
              <SelectItem key={candidate} value={candidate}>
                {holdReasonLabel(candidate)}
                {account.holds.some((hold) => hold.isActive && hold.reason === candidate)
                  ? ' — already held'
                  : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted">{holdReasonDescription(reason)}</p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="hold-amount">Amount</Label>
        <MoneyInput
          id="hold-amount"
          value={amount}
          onChange={setAmount}
          currency={account.currencyCode}
          aria-invalid={overCeiling}
        />
        <p className="text-xs text-muted">
          At most{' '}
          <MoneyDisplay value={ceiling} currency={account.currencyCode} className="inline text-left" />
          {existing ? ' — the unheld balance plus what this hold already holds.' : ' — the unheld balance.'}
        </p>
        {overCeiling ? (
          <p className="text-xs text-danger-text">
            That is more than the account has left to encumber. The server refuses it with
            savings.hold_exceeds_balance.
          </p>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="hold-note">Note</Label>
        <Input
          id="hold-note"
          value={note}
          autoComplete="off"
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
          aria-describedby="hold-note-help"
        />
        <p id="hold-note-help" className="text-xs text-muted">
          Required. It is the only explanation anyone reading this account in five years will have
          for why the money was held.
        </p>
      </div>
    </SavingsActionDialog>
  )
}

/**
 * Lifting a hold.
 *
 * The release reason is not carried on the hold record and does not come back on any read — it
 * lives in the audit trail under the hold's own entity, which is reachable from the hold's row. So
 * it is worth writing properly here: this is the only place it is ever captured.
 */
export function ReleaseHoldDialog({
  account,
  hold,
  open,
  onOpenChange,
}: {
  account: SavingsAccount
  hold: AccountHold | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [reason, setReason] = useState('')
  const release = useReleaseAccountHold(account.id)

  useEffect(() => {
    if (!open) {
      setReason('')
      release.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const trimmed = reason.trim()

  return (
    <SavingsActionDialog
      open={open && hold !== null}
      onOpenChange={onOpenChange}
      title="Release the hold"
      description="The amount returns to the available balance immediately, and can be withdrawn."
      notice={
        hold?.reason === 'OffsetPending' ? (
          <Alert tone="danger">
            <AlertTriangle aria-hidden />
            <div className="space-y-1">
              <AlertTitle>This hold was placed for a pending offset</AlertTitle>
              <AlertDescription>
                It is holding collateral while a guarantee offset against a defaulting member is
                decided. Releasing it here returns that money to the customer&apos;s available
                balance before the decision is made. Be certain the offset is settled.
              </AlertDescription>
            </div>
          </Alert>
        ) : null
      }
      confirmLabel="Release hold"
      tone="danger"
      isPending={release.isPending}
      disabled={trimmed === '' || hold === null}
      error={release.isError ? release.error : null}
      onConfirm={() => {
        if (hold === null) {
          return
        }

        release.mutate(
          { holdId: hold.id, reason: trimmed },
          {
            onSuccess: () => {
              onOpenChange(false)
            },
          },
        )
      }}
    >
      {hold ? (
        <div className="rounded-card border border-border-subtle bg-surface-muted p-3 text-sm">
          <p className="font-medium text-heading">{holdReasonLabel(hold.reason)}</p>
          <MoneyDisplay
            value={hold.amountMinor}
            currency={hold.currencyCode}
            emphasis="strong"
            className="text-left"
          />
          <p className="mt-1 text-xs text-body">
            {hold.note.trim() === '' ? 'No note was recorded.' : hold.note}
          </p>
        </div>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="release-reason">Why is it being released?</Label>
        <Input
          id="release-reason"
          value={reason}
          autoComplete="off"
          autoFocus
          maxLength={500}
          onChange={(event) => setReason(event.target.value)}
          aria-describedby="release-reason-help"
        />
        <p id="release-reason-help" className="text-xs text-muted">
          Required, and recorded only in the audit trail — no read of this account will ever show it
          again. Write it for the person who asks about this in a year.
        </p>
      </div>
    </SavingsActionDialog>
  )
}
